import React, { useEffect, useRef, useState } from 'react';

type Props = {
  label: string;
  signerName: string;
  savedImage?: string | null;
  canSign: boolean;
  busy?: boolean;
  onSave: (dataUrl: string) => Promise<void> | void;
};

const CANVAS_WIDTH = 560;
const CANVAS_HEIGHT = 180;

function eventPoint(event: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  const source = 'touches' in event ? event.touches[0] : event;
  if (!source) return null;
  return {
    x: ((source.clientX - rect.left) / rect.width) * canvas.width,
    y: ((source.clientY - rect.top) / rect.height) * canvas.height,
  };
}

const MobileSignaturePad: React.FC<Props> = ({
  label,
  signerName,
  savedImage,
  canSign,
  busy = false,
  onSave,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    setHasInk(false);
  };

  useEffect(() => {
    if (!open) return;
    clearCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closePopup = () => {
    if (busy) return;
    setOpen(false);
    hasInkRef.current = false;
    setHasInk(false);
  };

  const startDraw = (event: React.MouseEvent | React.TouchEvent) => {
    if (busy) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    const point = canvas ? eventPoint(event, canvas) : null;
    if (!canvas || !ctx || !point) return;
    drawingRef.current = true;
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 3.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const moveDraw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    const point = canvas ? eventPoint(event, canvas) : null;
    if (!ctx || !point) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setHasInk(true);
    }
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const acceptDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) return;
    setPreviewImage(canvas.toDataURL('image/png'));
    closePopup();
  };

  const discardPreview = () => {
    if (busy) return;
    setPreviewImage(null);
  };

  const confirmSignature = async () => {
    if (!previewImage || busy) return;
    try {
      await onSave(previewImage);
      setPreviewImage(null);
    } catch {
      // Lỗi do màn hợp đồng hiển thị.
    }
  };

  const displayImage = savedImage || previewImage;
  const pendingPreview = Boolean(canSign && previewImage && !savedImage);

  return (
    <div className="border-t border-[#e7dcc3] pt-4">
      <p className="mb-1 font-bold uppercase text-[#1f2937]">{label}</p>
      <p className="mb-3 text-sm text-gray-600">
        {savedImage ? 'Đã ký' : pendingPreview ? 'Xem chữ ký rồi xác nhận hoặc xóa' : canSign ? 'Nhấn vào ô để ký' : 'Chưa ký'}
      </p>
      {displayImage ? (
        <div className="flex h-24 items-center justify-center rounded-xl border border-[#dcc79d] bg-white px-3 py-2">
          <img src={displayImage} alt={`Chữ ký ${label}`} className="max-h-20 w-auto max-w-[220px] object-contain" />
        </div>
      ) : canSign ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => setOpen(true)}
          className="flex h-24 w-full items-center justify-center rounded-xl border-2 border-dashed border-[#C4A35A] bg-white text-sm font-medium text-[#C4A35A] disabled:opacity-60"
        >
          Nhấn để ký
        </button>
      ) : (
        <div className="flex h-24 items-center justify-center rounded-xl border border-[#dcc79d] bg-white text-sm text-gray-400">
          Chưa có chữ ký
        </div>
      )}

      {pendingPreview && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={discardPreview}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            Xóa
          </button>
          <button
            type="button"
            disabled={busy || !previewImage}
            onClick={() => void confirmSignature()}
            className="flex-1 rounded-lg bg-[#C4A35A] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? 'Đang lưu…' : 'Xác nhận'}
          </button>
        </div>
      )}

      <p className="mt-3 font-semibold text-gray-800">{signerName}</p>

      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
          <div className="flex w-full max-w-sm flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-2 border-b border-black/5 px-4 py-3">
              <h3 className="text-base font-semibold text-black">Ký {label.toLowerCase()}</h3>
              <button
                type="button"
                disabled={busy}
                onClick={closePopup}
                className="rounded-xl border border-black/10 px-3 py-1.5 text-sm font-medium text-black/70"
              >
                Đóng
              </button>
            </div>

            <div className="px-4 py-4">
              <div className="overflow-hidden rounded-xl border-2 border-dashed border-[#C4A35A] bg-white">
                <canvas
                  ref={canvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  className="h-36 w-full touch-none cursor-crosshair"
                  onMouseDown={startDraw}
                  onMouseMove={moveDraw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={moveDraw}
                  onTouchEnd={endDraw}
                />
              </div>
              <p className="mt-3 text-sm text-black/55">Vẽ chữ ký, bấm Đồng ý để hiện trên hợp đồng rồi xác nhận.</p>
            </div>

            <div className="flex gap-2 border-t border-black/5 px-4 py-3">
              <button
                type="button"
                disabled={busy || !hasInk}
                onClick={clearCanvas}
                className="flex-1 rounded-xl border border-black/10 px-3 py-2.5 text-sm font-medium text-black/70 disabled:opacity-50"
              >
                Xóa
              </button>
              <button
                type="button"
                disabled={busy || !hasInk}
                onClick={acceptDrawing}
                className="flex-1 rounded-xl bg-[#C4A35A] px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                Đồng ý
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSignaturePad;
